import { Box, Chip, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import uuidv4 from 'src/utils/uuidv4';
import MRNIcon from '../../../../assets/icons/mrn.svg';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import {
  Cake as CakeIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { usePatients } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useEffect, useMemo, useState } from 'react';
import { PATH_DASHBOARD } from 'src/routes/paths';
import Iconify from 'src/components/Iconify';
import { useNavigate } from 'react-router';
import { getChildOrganizations } from '../../case/detail/components/SearchMember/api';
import { isArray, isString } from 'lodash';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { getReferenceId } from 'src/utils/fhir';
import { useFuegoApp } from 'src/careflow/fuego_app/useFuegoApp';

type Props = {
  patient: WrappedPatient | null;
  i18n: i18nFunction;
};

export function LinkList({ patient, i18n }: Props) {
  const [openMenu, setOpenMenu] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [allowedOrganizations, setAllowedOrganizations] = useState<any[]>([]);

  const { viewOnFuego, mergeOnFuego } = useFuegoApp();
  const navigate = useNavigate();
  const user = useAuth();

  const [linkedPatients, { isFetching, isLoading }] = usePatients({
    filter: {
      _id: patient?.id,
      _include: 'Patient:link',
    },
    autofetch: !!patient,
    map: PatientWrapper,
  });

  const userOrgFhirId = useMemo(() => {
    if (user) {
      const selectedOrg = user.getSelectedOrg();
      if (
        selectedOrg &&
        selectedOrg?.fhir_uri &&
        isString(selectedOrg?.fhir_uri) &&
        selectedOrg?.fhir_uri.includes('Organization/')
      )
        try {
          return selectedOrg?.fhir_uri.split('Organization/')?.[1];
        } catch (error) {
          return null;
        }
    }
    return null;
  }, [user]);

  useEffect(() => {
    const getAllowedOrganizations = async () => {
      if (!userOrgFhirId) return;
      try {
        const result = await getChildOrganizations([userOrgFhirId]);
        if (result && isArray(result)) {
          setAllowedOrganizations(result);
        }
      } catch (error) {
        console.error('Error fetching child organizations:', error);
      }
    };

    getAllowedOrganizations();
  }, [userOrgFhirId]);

  const mappedLinkedPatients = useMemo(() => {
    const isSecondary = patient?.link?.some((l) => l?.type === 'refer');

    const masterId = patient?.link
      ?.find((l) => l?.type === 'refer')
      ?.other?.reference?.replace('Patient/', '');

    return linkedPatients
      ?.filter((p) => p?.id !== patient?.id)
      ?.map((p) => ({
        ...p,
        profile: isSecondary ? masterId : p.id,
      }));
  }, [linkedPatients, patient]);

  const handleCloseMenu = () => {
    setOpenMenu(null);
    setSelectedRow(null);
  };

  const handleViewDetails = (id: string) => {
    navigate(PATH_DASHBOARD.crs.patient.details.forId(id));
    handleCloseMenu();
  };

  const columns: GridColDef<WrappedPatient>[] = [
    {
      field: 'mrn',
      headerName: `${i18n('patients.details.link.list.mrn', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Tooltip title={i18n('patients.mrn', 'crs')}>
            <Typography variant="subtitle2">
              <MRNIcon style={{ height: 13, width: 13, marginRight: 9 }} />
              {row?.getMRN('')?.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'patient',
      headerName: `${i18n('patients.details.link.list.patient', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Stack direction="column">
            <Typography variant="subtitle2">
              <PersonIcon sx={{ marginRight: 1, marginBottom: '-2px' }} fontSize={'small'} />{' '}
              {row?.getFullName()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <CakeIcon
                sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
                fontSize={'small'}
              />
              {row?.birthDate ? `${row.getBirthDateForDisplay()} (${row.getAgeInYears()})` : null}
            </Typography>
            <Typography variant="body2">{row?.gender}</Typography>
          </Stack>
        );
      },
    },
    {
      field: 'contact',
      headerName: `${i18n('patients.details.link.list.contact', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Stack direction="column">
            <Typography variant="body2">
              <PhoneIcon
                sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
                fontSize={'small'}
              />
              {row?.getPrimaryPhone()?.value}
            </Typography>
            <Typography variant="body2">
              <EmailIcon
                sx={{ marginRight: 1, color: '#919eab', marginBottom: '-5px' }}
                fontSize={'small'}
              />
              {row?.getPrimaryEmail()?.value}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: `${i18n('patients.details.link.list.status', 'crs')}`,
      flex: 0.4,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        const status = row?.active
          ? i18n('patients.details.link.list.active', 'crs')
          : i18n('patients.details.link.list.inactive', 'crs');
        return (
          <>
            <Chip
              size="small"
              label={status}
              color={(row?.active && 'success') || 'error'}
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          </>
        );
      },
    },
    {
      field: 'organization',
      headerName: `${i18n('patients.details.link.list.organization', 'crs')}`,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        return <Typography variant="body2">{row?.getOrganization()}</Typography>;
      },
    },
    {
      field: 'details',
      headerName: '',
      flex: 0.2,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as any;
        const isAllowed = allowedOrganizations?.some(
          (e) => getReferenceId(row?.managingOrganization?.reference) === e
        );
        return (
          <>
            <IconButton
              onClick={(event) => {
                setOpenMenu(event.currentTarget);
                setSelectedRow(row);
              }}
              disabled={!isAllowed}
              sx={{
                color: '#637381',
                '&:hover': {
                  backgroundColor: 'rgba(99, 115, 129, 0.08)',
                },
              }}
            >
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>

            <Menu
              anchorEl={openMenu}
              open={Boolean(openMenu && selectedRow?.id === row?.id)}
              onClose={handleCloseMenu}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => handleViewDetails(row?.profile)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(99, 115, 129, 0.08)',
                  },
                }}
                disabled={!row?.profile}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <OpenInNewIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {i18n('patients.details.link.list.details', 'crs')}
                  </Typography>
                </Stack>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  viewOnFuego({
                    target: 'patient',
                    primaryPatientId: patient?.id,
                    targetPatientId: row?.id || row?.profile,
                    currentPatientName: patient?.getFullName?.() || undefined,
                    targetPatientName: row?.getFullName?.() || undefined,
                  });
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(99, 115, 129, 0.08)',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Iconify icon="eva:external-link-fill" sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2">
                    View on Fuego App
                  </Typography>
                </Stack>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  mergeOnFuego({
                    target: 'patient',
                    primaryPatientId: patient?.id,
                    targetPatientId: row?.id || row?.profile,
                    currentPatientName: patient?.getFullName?.() || undefined,
                    targetPatientName: row?.getFullName?.() || undefined,
                  });
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(99, 115, 129, 0.08)',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Iconify icon="mdi:call-merge" sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2">
                    Merge on Fuego App
                  </Typography>
                </Stack>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  return (
    <Stack direction="column">
      <Box sx={{ width: '100%', height: 350 }}>
        <DataGrid
          rows={mappedLinkedPatients}
          columns={columns}
          loading={isFetching && isLoading}
          rowSpacingType="margin"
          getRowId={() => uuidv4()}
          getRowSpacing={() => ({ bottom: 5 })}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 90}
          isRowSelectable={() => false}
          disableColumnFilter
          disableColumnMenu
          density={'compact'}
          components={{
            Footer: () => <></>,
          }}
          className="dataGridWithFilter"
          sx={[
            {
              width: '100%',
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid #EAECF0',
                '&:nth-of-type(odd)': {
                  backgroundColor: '#FAFAFA',
                },
                '&:hover': {
                  backgroundColor: '#F5F6F7',
                },
              },
            },
          ]}
        />
      </Box>
    </Stack>
  );
}
